import { useCurrentAsset } from '@/hooks/use-current-asset';

import type { Domain } from '../components/filters/domain-toggle';
import type { Filters } from '../entities/filters';
import type { Node } from '../entities/node';

function findNodeById(id: string, level: string, nodesData?: Node[]): Node {
  const foundNode = nodesData?.find(
    (node) => node.id === id && node.level === level,
  );
  if (foundNode) {
    return foundNode;
  }

  // Fallback: create node with id as both id and label (backward compatibility)
  return {
    id,
    label: id,
    source: '',
    level,
  };
}

export function deriveCurrentNode(
  filters: Filters,
  domain: Domain,
  assetId?: string,
  nodesData?: Node[],
): Node | undefined {
  const nodes = deriveCurrentNodes(filters, domain, assetId, nodesData);
  return nodes.length > 0 ? nodes[0] : undefined;
}

export function deriveCurrentNodes(
  filters: Filters,
  domain: Domain,
  assetId?: string,
  nodesData?: Node[],
): Node[] {
  const { batteryFilters, pcsFilters } = filters;

  if (domain === 'battery') {
    const { cellId, moduleId, packId, bankId, containerId, nodeId, arrayId } =
      batteryFilters;

    // Check each level in reverse hierarchy order (deepest first)
    const levelChecks = [
      { id: cellId, level: 'cell' },
      { id: moduleId, level: 'module' },
      { id: packId, level: 'pack' },
      { id: bankId, level: 'bank' },
      { id: containerId, level: 'container' },
      { id: nodeId, level: 'node' },
      { id: arrayId, level: 'array' },
    ];

    for (const { id, level } of levelChecks) {
      if (id) {
        // Handle both single and multiple selections
        const ids = Array.isArray(id) ? id : [id];
        return ids.map((singleId) => findNodeById(singleId, level, nodesData));
      }
    }

    // Default to system level when no battery filters are selected
    // (system level is filtered out of UI but can still be queried for data)
    if (assetId) {
      return [findNodeById(assetId, 'system', nodesData)];
    }
  } else {
    const { pcsId } = pcsFilters;
    if (pcsId) {
      // Handle both single and multiple selections for PCS
      const ids = Array.isArray(pcsId) ? pcsId : [pcsId];
      return ids.map((singleId) => findNodeById(singleId, 'pcs', nodesData));
    }

    // Default to system level when no PCS filters are selected
    // (system level is filtered out of UI but can still be queried for data)
    if (assetId) {
      return [findNodeById(assetId, 'system', nodesData)];
    }
  }

  return [];
}

export function useDeriveCurrentNode(
  filters: Filters,
  domain: Domain,
  nodesData?: Node[],
): Node | undefined {
  const { accureId } = useCurrentAsset();

  return deriveCurrentNode(filters, domain, accureId, nodesData);
}

export function useDeriveCurrentNodes(
  filters: Filters,
  domain: Domain,
  nodesData?: Node[],
): Node[] {
  const { accureId } = useCurrentAsset();

  return deriveCurrentNodes(filters, domain, accureId, nodesData);
}
